import React from 'react';
import styled from 'styled-components';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const Container = styled.div`
  margin: 20px 0;
  background-color: #f8f9fa;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Title = styled.h2`
  color: #0078d4;
  font-size: 1.8rem;
  margin-bottom: 25px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 20px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 25px;
  
  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const SecurePaymentNote = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 10px;
  padding: 12px;
  background-color: #f1f8e9;
  border-radius: 8px;
  color: #4caf50;
  font-size: 0.95rem;
  font-weight: 500;
`;

const CardContainer = styled.div`
  border: 1px solid #ddd;
  border-radius: 10px;
  padding: 20px;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:focus-within {
    border-color: #0078d4;
    box-shadow: 0 0 0 3px rgba(0, 120, 212, 0.2);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 10px;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled.button<{ primary?: boolean }>`
  background-color: ${props => props.primary ? '#0078d4' : '#f5f5f5'};
  color: ${props => props.primary ? 'white' : '#333'};
  border: none;
  border-radius: 8px;
  padding: 15px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1;
  box-shadow: ${props => props.primary ? '0 4px 8px rgba(0, 120, 212, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.05)'};
  
  &:hover {
    background-color: ${props => props.primary ? '#005a9e' : '#e0e0e0'};
    transform: translateY(-2px);
    box-shadow: ${props => props.primary ? '0 6px 12px rgba(0, 120, 212, 0.3)' : '0 4px 8px rgba(0, 0, 0, 0.1)'};
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: 768px) {
    padding: 12px;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    
    &:last-child {
      order: -1; /* Poner el botón primario arriba en móviles */
    }
  }
`;

const Icon = styled.span`
  font-size: 1.2rem;
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  padding: 15px;
  background-color: #ffebee;
  border-radius: 8px;
  font-size: 0.95rem;
  border-left: 4px solid #d32f2f;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const PrecioTotal = styled.div`
  font-size: 1.2rem;
  font-weight: 500;
  display: flex;
  justify-content: space-between;
  padding: 15px;
  background-color: #e6f7ff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 5px;
`;

const PaymentMethods = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;
  
  img {
    height: 30px;
    opacity: 0.7;
  }
`;

const InfoText = styled.p`
  text-align: center;
  font-size: 0.9rem;
  color: #666;
  margin-top: 20px;
  line-height: 1.5;
`;

// Cargar Stripe (normalmente este stripePromise estaría en un archivo separado)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

interface FormularioPagoProps {
  clientSecret: string;
  precio: number;
  onSuccess: (paymentIntentId: string) => void;
  onCancel: () => void;
}

const FormularioPago: React.FC<FormularioPagoProps> = ({ 
  clientSecret, 
  precio, 
  onSuccess, 
  onCancel 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = React.useState<string | null>(null);
  const [processing, setProcessing] = React.useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    // Obtener el elemento de la tarjeta
    const cardElement = elements.getElement(CardElement);
    
    if (!cardElement) {
      setError('No se pudo procesar el pago. Inténtalo de nuevo más tarde.');
      setProcessing(false);
      return;
    }

    // Confirmar el pago
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
      }
    });

    if (error) {
      setError(error.message || 'Hubo un error al procesar el pago');
      setProcessing(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    } else {
      setError('El pago no pudo ser procesado. Por favor, inténtalo de nuevo.');
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        fontFamily: 'Arial, sans-serif',
        '::placeholder': {
          color: '#aab7c4',
        },
        padding: '10px 0',
      },
      invalid: {
        color: '#9e2146',
        iconColor: '#9e2146',
      },
    },
    hidePostalCode: true,
  };

  return (
    <Form onSubmit={handleSubmit}>
      <SecurePaymentNote>
        <Icon>🔒</Icon> Tu pago es seguro y está encriptado
      </SecurePaymentNote>
      
      <PrecioTotal>
        <span>Total a pagar:</span>
        <span>{precio.toFixed(2)}€</span>
      </PrecioTotal>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <CardContainer>
        <CardElement options={cardElementOptions} />
      </CardContainer>
      
      <ButtonContainer>
        <Button type="button" onClick={onCancel} disabled={processing}>
          <Icon>←</Icon> Volver
        </Button>
        <Button type="submit" primary disabled={!stripe || processing}>
          {processing ? (
            <>Procesando... <Icon>⏳</Icon></>
          ) : (
            <>Pagar ahora <Icon>💳</Icon></>
          )}
        </Button>
      </ButtonContainer>
      
      <PaymentMethods>
        <img src="https://cdn.jsdelivr.net/gh/stephenmirving/stripe-payment-icons/assets/visa.svg" alt="Visa" />
        <img src="https://cdn.jsdelivr.net/gh/stephenmirving/stripe-payment-icons/assets/mastercard.svg" alt="Mastercard" />
        <img src="https://cdn.jsdelivr.net/gh/stephenmirving/stripe-payment-icons/assets/amex.svg" alt="American Express" />
      </PaymentMethods>
      
      <InfoText>
        No se realizará ningún cargo hasta confirmar el pago. 
        Tus datos de tarjeta están seguros y encriptados.
      </InfoText>
    </Form>
  );
};

interface PagoStripeProps {
  precio: number;
  clientSecret: string;
  onPaymentSuccess: (paymentIntentId: string) => void;
  onPaymentCancel: () => void;
}

const PagoStripe: React.FC<PagoStripeProps> = ({
  precio,
  clientSecret,
  onPaymentSuccess,
  onPaymentCancel
}) => {
  return (
    <Container>
      <Title>Pago Seguro</Title>
      <Elements stripe={stripePromise}>
        <FormularioPago 
          clientSecret={clientSecret}
          precio={precio}
          onSuccess={onPaymentSuccess}
          onCancel={onPaymentCancel}
        />
      </Elements>
    </Container>
  );
};

export default PagoStripe; 